/**
 * tab/AtrzDocBoxMbrTab.tsx
 * 조합원 결재 문서함
 */

import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";
import Button from "@/js/common/ui/Button";
import GridTable, { fldInfsProps } from "@/js/common/ui/GridTable";
import Modal from "@/js/common/ui/modal/Modal";
import AdminGridStyle from "@/js/common/ui/style/AdminGridStyle";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import SelectBox from "@/js/common/ui/input/SelectBox";
import TextBox from "@/js/common/ui/input/TextBox";
import DialogTitleBar from "@/js/system/common/DialogTitleBar";
import MbrAtrzRejectReasonDialog from "../dialog/MbrAtrzRejectReasonDialog";

import MbrasNewHistory from "../history/MbrasNewHistory";
import MbrasWhdwlHistory from "../history/MbrasWhdwlHistory";
import MbrasRegulHistory from "../history/MbrasRegulHistory";
import MbrasDepartHistory from "../history/MbrasDepartHistory";
import MbrasLeaveHistory from "../history/MbrasLeaveHistory";
import MbrasLeaveExtendHistory from "../history/MbrasLeaveExtendHistory";
import MbrasReturnHistory from "../history/MbrasReturnHistory";
import MbrasResignHistory from "../history/MbrasResignHistory";
import MbrasRetireHistory from "../history/MbrasRetireHistory";
import MbrasRejoinHistory from "../history/MbrasRejoinHistory";

interface Props {
  processing: boolean;
  setProcessing: Function;
}

const AtrzDocBoxMbrTab = ({processing, setProcessing}: Props) => {
  const [keyword, setKeyword] = React.useState("");
  const [mbrasAtrzSeCd, setMbrasAtrzSeCd] = React.useState("");
  const [upOgnzCd, _setUpOgnzCd] = React.useState("");
  const [ognzCd, _setOgnzCd] = React.useState("");
  const [sttDt, setSttDt] = React.useState("");
  const [endDt, setEndDt] = React.useState("");

  const [dataArr, setDataArr] = React.useState<any[]>([]);
  const [checkDataArr, setCheckDataArr] = React.useState<any[]>([]);
  const confirmedDocIdRef = React.useRef<string>("");  // 확인 후 해당 문서를 체크 처리하기 위한 상태값
  const [callTime, setCallTime] = React.useState<Date>(new Date());

  const [detailModalOpen, setDetailModalOpen] = React.useState(false);
  const [mbrasId, setMbrasId] = React.useState<string>("");
  const [mbrasAtrzDocId, setMbrasAtrzDocId] = React.useState<string>("");
  const [detailAtrzSeCd, setDetailAtrzSeCd] = React.useState<string>("");  // 결재문서 구분 코드(01신규, 03탈퇴 등)

  const [rejectModalOpen, setRejectModalOpen] = React.useState(false);
  const [rejectPayload, setRejectPayload] = React.useState<any>(null);

  const fldInfs: fldInfsProps[] = [
      { type: "seq" ,                          width:  50, headAlign: "center", dataAlign: "center", label: "No." },
      { type: "el"  , fldNm: "mbrasAtrzSeCd" , width:  90, headAlign: "center", dataAlign: "center", label: "구분", el: (item: any) => <span className={"ls06 " + (item.mbrasAtrzSeCd === "03" ? "fcE53935" : "")}>{Util.getValToCode("MBRAS_ATRZ_SE_CD", item.mbrasAtrzSeCd)}</span> },
      { type: "text", fldNm: "mbrasNm"       , width:  90, headAlign: "center", dataAlign: "center", label: "성명" },
      { type: "text", fldNm: "upOgnzNm"      , width: 130, headAlign: "center", dataAlign: "center", label: "본부" },
      { type: "text", fldNm: "ognzNm"        , width: 130, headAlign: "center", dataAlign: "center", label: "지부" },
      { type: "code", fldNm: "ocptTypeCd"    , width: 110, headAlign: "center", dataAlign: "center", label: "직종", catCode: "OCPT_TYPE_CD" },
      { type: "date", fldNm: "applyDt"       , width: 120, headAlign: "center", dataAlign: "center", label: "신청일" },
      { type: "date", fldNm: "atrzReqDt"     , width: 120, headAlign: "center", dataAlign: "center", label: "상신일" },
      { type: "code", fldNm: "atrzSttsCd"    , width:  90, headAlign: "center", dataAlign: "center", label: "상태", catCode: "ATRZ_STTS_CD" },
      { type: "el"  , fldNm: "docIdntyYn"    , width:  90, headAlign: "center", dataAlign: "center", label: "확인여부", el: (item: any) => <span className={"ls06 " + (item.docIdntyYn === "Y" ? "fc007bff" : "")}>{Util.getValToCode("DOC_IDNTY_YN_CD", item.docIdntyYn)}</span> },
  ];

  React.useEffect(() => {
    // 신청일자자 검색 조건 확인
    if (Util.isNotEmpty(sttDt) && Util.isNotEmpty(endDt)) {
        if (sttDt > endDt) {
            AppContext.openAlert({
                title: "상신일자 검색 조건 확인",
                msg: "상신일자의 검색 시작일자가 종료일자보다 느립니다.",
                icon: "warning"
            });
            setEndDt("")
            return;
        } 
    }
    AppContext.showSpinner();
    Axios.dataAccess({
      url: "system/atrz/mbras/select-aprvr-mbras-list.do",
      methodType: "post",
      paramType: "json",
      paramData: {
        mbrasAtrzSeCd: mbrasAtrzSeCd,
        upOgnzCd: upOgnzCd,
        ognzCd: ognzCd,
        sttDt: sttDt,
        endDt: endDt,
      },
      onSuccessFn: (res: any) => {
        const list = Array.isArray(res?.result?.list) ? res.result.list : [];
        setDataArr(list);
        // 기존에 체크되어 있던 문서 ID들을 Set으로 변환
        // 중복 방지 및 빠른 포함 여부 판단을 위해 Set 사용
        const selectedDocIds = new Set(
          (checkDataArr ?? [])
            .map((x: any) => String(x?.mbrasAtrzDocId ?? ""))
            .filter((id: string) => Util.isNotEmpty(id))
        );
        // 방금 '확인' 버튼으로 처리한 문서가 있다면 체크 대상에 추가
        if (Util.isNotEmpty(confirmedDocIdRef.current)) {
          selectedDocIds.add(String(confirmedDocIdRef.current));
        }
        // 새로 조회된 목록 기준으로 체크 상태를 재구성
        // 현재 목록에 존재하면서 selectedDocIds에 포함된 항목만 체크 배열로 생성
        const nextChecked = list
          .map((item: any, index: number) => ({
            ...item,
            checkBoxPk: index, // GridTable 내부 체크 식별용 값
          }))
          .filter((item: any) =>
            selectedDocIds.has(String(item?.mbrasAtrzDocId ?? ""))
          );
        // 체크 상태 반영
        setCheckDataArr(nextChecked);
        // 확인 처리용 ref는 1회성 사용이므로 초기화
        confirmedDocIdRef.current = "";
        // 로딩 종료
        AppContext.hideSpinner();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callTime, mbrasAtrzSeCd, upOgnzCd, ognzCd, sttDt, endDt]);

  const handleConfirm = () => {  // 결재 문서 확인 처리
      if (Util.isEmpty(mbrasAtrzDocId)) return;

      AppContext.showSpinner();

      Axios.dataAccess({
          url       : "system/atrz/update-mbras-aprvr-idnty.do",
          methodType: "post",
          paramType : "json",
          paramData : {
              mbrasAtrzDocId: mbrasAtrzDocId,
          },
          onSuccessFn: () => {
              AppContext.hideSpinner();

              AppContext.openAlert({
                  title: "알림",
                  msg  : "확인 처리되었습니다.",
                  icon : "info",
              });

              confirmedDocIdRef.current = String(mbrasAtrzDocId);
              setCallTime(new Date());
              setDetailModalOpen(false);
          },
      });
  };

  const openReject = () => { // 반려 모달 호출
      if (Util.isEmpty(mbrasAtrzDocId)) {
          AppContext.openAlert({ title: "데이터 확인", msg: "문서ID가 없습니다.", icon: "warning" });
          return;
      }

      setDetailModalOpen(false);

      setRejectPayload({
          mbrasAtrzDocId: mbrasAtrzDocId,
      });

      setRejectModalOpen(true);
  };

  const doApprove = () => {
    if (processing) return;

    if ((checkDataArr.length) === 0) {
      AppContext.openAlert({
        title: "조합원 선택",
        msg: "결재 처리할 조합원을 선택해주세요.",
        icon: "warning",
      });
      return;
    }

    // 결재 처리 시 체크된 항목들의 확인 여부 체크 로직
    const hasNotIdentified = (checkDataArr).some((x: any) => x.docIdntyYn !== "Y");
    if (hasNotIdentified) {
      AppContext.openAlert({
        title: "결재 불가",
        msg: "확인되지 않은 조합원이 있어 결재를 진행할 수 없습니다.",
        icon: "warning",
      });
      return;
    }

    AppContext.openAlert({
      title: "결재 처리",
      msg: "선택한 조합원을 결재 처리하시겠습니까?",
      icon: "check",
      confirmText: "결재",
      closeText: "취소",
      handleConfirm: () => {
        setProcessing(true);
        AppContext.showSpinner();

        Axios.dataAccess({
          url: "system/atrz/mbras/process-aprvr-mbras-atrz.do",
          methodType: "post",
          paramType: "json",
          paramData: { dataList: checkDataArr },
          onSuccessFn: () => {
            AppContext.hideSpinner();
            setProcessing(false);

            AppContext.openAlert({
              title: "알림",
              msg: "결재가 완료되었습니다.",
              icon: "info",
            });

            setCheckDataArr([]);
            setCallTime(new Date());
          },
        });
      },
    });
  };

  const clearForm = () => {
    setKeyword("");
    setMbrasAtrzSeCd("");
    setSttDt("");
    setEndDt("");
  };

  return (
    <div>
      <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
        <div className="dpFlx aiC gap14 mb10">
          <div className="dpFlx aiC">
            <span className="w40 fm-c fs15 fc333333 ls1 taL">성명</span>
            <TextBox
              value={keyword}
              width={180}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              style={{ backgroundColor: "#fff", padding: "0 10px" }}
              placeholder="성명 입력"
              onChangeFunc={(text: string) => setKeyword(text)}
              onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter") setCallTime(new Date());
              }}
            />
          </div>

          <div className="dpFlx aiC">
            <span className="w40 fm-c fs15 fc333333 ls1 taL">구분</span>
            <SelectBox
              value={mbrasAtrzSeCd}
              width={120}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              background="#fff"
              codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("UI_MBRAS_ATRZ_SE_CD_01"))}
              placeholder="전체"
              paddingRight={8}
              onChangeFunc={(data: string) => setMbrasAtrzSeCd(data)}
            />
          </div>

          <div className="dpFlx aiC">
            <span className="w80 fm-c fs15 fc333333 ls1 taL">상신일자</span>
            <DatePickerBox
              width={150}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              bgColor="white"
              textAlign="left"
              calendarThemeColor={AppContext.themeColor}
              className="mr5"
              value={sttDt}
              onChange={(data: string) => setSttDt(data)}
            />
            <span className="fm-c fc666666 mr5">~</span>
            <DatePickerBox
              width={150}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              bgColor="white"
              textAlign="left"
              calendarThemeColor={AppContext.themeColor}
              className="mr5"
              value={endDt}
              onChange={(data: string) => setEndDt(data)}
            />
          </div>

          <div className="dpFlx aiC mlA gap8">
            <Button
              width={70}
              height={40}
              fontFamily="fm-b"
              fontSize={16}
              letterSpacing={-1}
              variant="styInitBtn"
              borderRadius={5}
              buttonClass="dpFlx aiC jcC"
              buttonName="초기화"
              onClickFunc={clearForm}
            />
          </div>
        </div>
      </div>

      <div className="dpFlx aiC jcSb mb10 h44">
        <div className="fm-b fs16 fc007bff ls1 fw700">
          <span>조합원 결재 대기 목록</span>
        </div>
        <Button
          width={110}
          height={36}
          fontFamily="fm-b"
          fontSize={14}
          letterSpacing={-1}
          variant="styCompleteBtn"
          borderRadius={6}
          buttonClass="dpFlx aiC jcC"
          buttonName={processing ? "처리중..." : "결재처리"}
          onClickFunc={doApprove}
          disabled={processing}
        />
      </div>

      <GridTable
        tableInfo={
          {
            tableHeadVsb: true,
            styleType: AdminGridStyle,
            fldInfs,
            ordFld: { name: "atrzReqDt", direction: "desc" },
            dataArr: Util.filterFieldData(dataArr, fldInfs, "mbrasNm", keyword),
            maxRowNums: [10, 20, 30],
            theadTrHeight: 40,
            tbodyTrHeight: 35,
            paginateClass: "pt10",
            paginateStyle: {
              buttonOnColor: AppContext.boardThemeColor,
              buttonWidth: 22,
              buttonHeight: 24,
              buttonMarginLeft: 1,
              buttonMarginRight: 1,
              fontSize: 13,
              color: "#666666",
              letterSpacing: -0.8,
              fontFamily: "Pretendard-Regular",
              borderRadius: 3,
            },
            footerVsb: true,
            checkedHookParam: {
              checkDataArr: checkDataArr,
              setCheckDataArr: setCheckDataArr,
            },
            rowClickFunc: (row: any) => {
              setMbrasId(String(row.mbrasId));
              setMbrasAtrzDocId(String(row.mbrasAtrzDocId));
              setDetailAtrzSeCd(String(row.mbrasAtrzSeCd));
              setDetailModalOpen(true);
            },
          } as any
        }
      />

      <Modal
        modalOpen={detailModalOpen}
        dim={true}
        visible={true}
        width={760}
        titleEl={<DialogTitleBar title={"조합원 결재 상세"} backgroundColor={AppContext.themeColor} />}
        onCloseFunc={() => {
          setMbrasId("");
          setMbrasAtrzDocId("");
          setDetailAtrzSeCd("");
          setDetailModalOpen(false);
        }}
      >
        <div className="h900">
          {detailAtrzSeCd === "01" && (
            <MbrasNewHistory
              mbrasId={mbrasId}
              mbrasAtrzDocId={mbrasAtrzDocId}
            />
          )}

          {detailAtrzSeCd === "03" && (
            <MbrasWhdwlHistory
              mbrasId={mbrasId}
              mbrasAtrzDocId={mbrasAtrzDocId}
            />
          )}

          {detailAtrzSeCd === "04" && (
            <MbrasRegulHistory
              mbrasId={mbrasId}
              mbrasAtrzDocId={mbrasAtrzDocId}
            />
          )}

          {detailAtrzSeCd === "05" && (
            <MbrasDepartHistory
              mbrasId={mbrasId}
              mbrasAtrzDocId={mbrasAtrzDocId}
            />
          )}

          {detailAtrzSeCd === "06" && (
            <MbrasLeaveHistory
              mbrasId={mbrasId}
              mbrasAtrzDocId={mbrasAtrzDocId}
            />
          )}

          {detailAtrzSeCd === "07" && (
            <MbrasLeaveExtendHistory
              mbrasId={mbrasId}
              mbrasAtrzDocId={mbrasAtrzDocId}
            />
          )}

          {detailAtrzSeCd === "08" && (
            <MbrasReturnHistory
              mbrasId={mbrasId}
              mbrasAtrzDocId={mbrasAtrzDocId}
            />
          )}

          {detailAtrzSeCd === "09" && (
            <MbrasResignHistory
              mbrasId={mbrasId}
              mbrasAtrzDocId={mbrasAtrzDocId}
            />
          )}

          {detailAtrzSeCd === "10" && (
            <MbrasRetireHistory
              mbrasId={mbrasId}
              mbrasAtrzDocId={mbrasAtrzDocId}
            />
          )}

          {detailAtrzSeCd === "11" && (
            <MbrasRejoinHistory
              mbrasId={mbrasId}
              mbrasAtrzDocId={mbrasAtrzDocId}
            />
          )}
        </div>

        <div className="dpFlx jcC mt20 mb10 gap10">
            <Button
                width={100}
                height={40}
                fontFamily="fm-b"
                fontSize={16}
                letterSpacing={-1}
                variant="styCloseBtn"
                borderRadius={6}
                buttonClass="dpFlx aiC jcC"
                buttonName="닫기"
                onClickFunc={() => setDetailModalOpen(false)}
            />

            <Button
                width={100}
                height={40}
                fontFamily="fm-b"
                fontSize={16}
                letterSpacing={-1}
                variant="styDeleteBtn"
                borderRadius={6}
                buttonClass="dpFlx aiC jcC"
                buttonName="반려"
                onClickFunc={openReject}
            />

            <Button
                width={100}
                height={40}
                fontFamily="fm-b"
                fontSize={16}
                letterSpacing={-1}
                variant="styTempBtn"
                borderRadius={6}
                buttonClass="dpFlx aiC jcC"
                buttonName="확인"
                onClickFunc={handleConfirm}
            />
        </div>
      </Modal>

      <Modal
        modalOpen={rejectModalOpen}
        dim={true}
        visible={true}
        titleEl={<DialogTitleBar title={"반려 사유"} backgroundColor={AppContext.themeColor} />}
        onCloseFunc={() => {
          setRejectModalOpen(false);
          setDetailModalOpen(true);
          setRejectPayload(null);
        }}
      >
        <MbrAtrzRejectReasonDialog
          mbrasAtrzDocId={String(rejectPayload?.mbrasAtrzDocId ?? "")}
          setRejectModalOpen={setRejectModalOpen}
          setDetailModalOpen={setDetailModalOpen}
          setCallTime={setCallTime}
        />
      </Modal>
    </div>
  );
};

export default AtrzDocBoxMbrTab;
